<template> 
	<div class="SpectrogramPlayer" :class="classes" dir="ltr" tabindex="0">
		<div
			class="SpectrogramPlayer-frame"
			aria-hidden="true"
			@click.prevent.capture="!annotation ? togglePlay() : false"
			:style="{ gridTemplateColumns: `${yAxisWidth}px 1fr`, gridTemplateRows: `${xAxisHeight}px ${frameHeight}px auto ${timelineHeight}px` }">
			<img
				class="SpectrogramPlayer-poster"
				role="presentation"
				:alt="'ML' + assetId + ' visualization'"
				v-if="!autoplay && !showSpectrogramPlayer && !mediaMissing"
				:src="poster"
				:class="{ offset: posterOffset }"
				@click="togglePlay"
				@error="mediaMissing = true"
				:style="{ height: frameHeight + 'px', top: xAxisHeight + 'px' }"
			/>
			<div v-if="showLoading && !mediaMissing && !stillProcessing && !minimal" class="LoadingAnimation">
				<CloIcon symbol="spinner" />
			</div>
			<div v-if="stillProcessing && !mediaMissing" class="SpectrogramPlayer-errorMessage">
				<CloIcon symbol="timer" />
				{{ messages.errorNoSpectrogram }}
			</div>
			<div v-if="mediaMissing" class="SpectrogramPlayer-errorMessage">
				<CloIcon symbol="timer" />
				{{ messages.errorNoAudio }}
			</div>
			<canvas 
				v-show="showSpectrogramPlayer && !minimal" 
				ref="xAxisCanv" 
				class="xAxisCanv" 
				aria-hidden="true" 
				:width="width * dpr"
				:height="xAxisHeight * dpr" 
				@click="togglePlay"
				:style="{
					width: `${width}px`,
					height: `${xAxisHeight}px`
				}"
			>
			</canvas>
			<CloAudioGuides 
				:guides="guides"
				:offset="time * pxPerSec / width"
				:width="width"
				:zoom="zoomLevel"
				:basePxPerSec="basePxPerSec"
			/>
			<canvas
				v-show="showSpectrogramPlayer"
				ref="trackCanv"
				class="trackCanv"
				aria-hidden="true"
				:width="width * dpr"
				:height="frameHeight * dpr"
				@click="togglePlay"
				:style="{
					...annotation ? {
						cursor: 'pointer'
					} : {},
					width: `${width}px`,
					height: `${frameHeight}px`
				}"
			></canvas>
			<CloAudioAnnotations
				v-if="annotation && duration"
				class="trackCanv"
				:width="width"
				:height="frameHeight"
				:enableAnnotating="enableAnnotating"
				:initialAnnotations="initialAnnotations"
				:activeAnnotation="activeAnnotation"
				:alternateHandles="alternateHandles"
				:edgeHandles="edgeHandles"
				v-on:annotation-changed="saveAnnotation"
				v-on:annotation-selected="annotationSelected"
				:showAnnotations="true"
				:offset="time * pxPerSec / width"
				:totalOffset="duration * pxPerSec / width"
				:zoom="zoomLevel"
				:annotationVersion="version"
				:maxFrequency="maxFreq"
				:basePxPerSec="basePxPerSec"
				:clonePosition="'x'"
			/>
			<CloModelPredictions
				v-if="predictions.length && duration"
				class="SpectrogramPlayer-predictions"
				:predictions="predictions"
				:offset="time * pxPerSec / width"
				:width="width"
				:zoom="zoomLevel"
				:basePxPerSec="basePxPerSec"
				ref="predictions"
				@prediction-click="$emit('prediction-click', $event)"
			/>
			<canvas
				v-show="showSpectrogramPlayer"
				ref="timelineCanv"
				class="timelineCanv"
				aria-hidden="true"
				:width="(width + yAxisWidth) * dpr"
				:height="timelineHeight * dpr"
				:style="{ 
					backgroundSize: timelineImageWidth + 'px ' + timelineHeight + 'px', 
					backgroundImage: timelineImage,
					width: `${width + yAxisWidth}px`,
					height: `${timelineHeight}px`
				}"
			></canvas>
			<canvas 
				v-show="showSpectrogramPlayer" 
				ref="cursorCanv" 
				class="cursorCanv" 
				aria-hidden="true" 
				:width="width * dpr" 
				:height="(frameHeight + xAxisHeight + predictionsHeight) * dpr"
				:style="{
					width: `${width}px`,
					height: `${frameHeight + xAxisHeight + predictionsHeight}px`
				}"
			></canvas>
			<canvas 
				v-show="showSpectrogramPlayer && !minimal" 
				ref="yAxisCanv" 
				class="yAxisCanv" 
				aria-hidden="true" 
				:width="yAxisWidth * dpr" 
				:height="(frameHeight + xAxisHeight) * dpr"
				:style="{
					height: `${frameHeight + xAxisHeight}px`,
					width: `${yAxisWidth}px`
				}"
			></canvas>
			<audio
				v-bind:aria-label="'ML' + assetId"
				:src="media"
				ref="audio"
				@timeupdate="timeUpdated"
				@loadedmetadata="loadedMetadata"
				@playing="isPlaying"
				@pause="isPaused"
				@canplaythrough.once="canPlayThrough"
				preload="metadata"
				@error="mediaMissing = true"
			></audio>
		</div>
		<!-- CloMediaControls -->
		<CloMediaControls
			v-if="!minimal"
			:playing="playing"
			:messages="{
				seek: 'seek',
				play: 'play',
				fullscreen: 'fullscreen',
				volume: 'volume',
				mute: 'mute',
			}"
			:showVolume="true"
			:citationUrl="citationUrl"
			:citationName="citationName"
			:title="title"
			:user="user"
			:date="date"
			:location="location"
			v-on:togglePlay="togglePlay"
			:assetId="assetId"
			:progressPercent="progressPercent"
			v-on:setPosition="setPosition"
			v-on:setVolume="setVolume"
			v-on:setTime="setTime"
			v-on:toggleVolume="toggleVolume"
			v-on:set-zoom="setZoom"
			:duration="duration"
			:currentTime="time"
			:timeline="timeline"
			:zoom="zoom && showSpectrogramPlayer"
            :baseZoomLevel="baseZoomLevel"
		/>
	</div>
</template>

<script>
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation.vue';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloAudioAnnotations from '@/media/CloSpectrogramPlayer/CloAudioAnnotations.vue';
import CloModelPredictions from '@/media/CloSpectrogramPlayer/CloModelPredictions.vue';
import CloAudioGuides from '@/media/CloSpectrogramPlayer/CloAudioGuides.vue';
import CloMediaControls from '@/media/CloMediaControls/CloMediaControls.vue';

export default {
	components: {
		CloLoadingAnimation,
		CloMediaControls,
		CloIcon,
		CloAudioAnnotations,
		CloModelPredictions,
		CloAudioGuides
	},

	data() {
		return {
			annotations: [],
			showSpectrogramPlayer: true,
			readyToPlaySpect: false,
			readyToPlayAudio: false,
			playing: false,
			needToAutoplay: false,
			dpr: 1,
			position: 0,
			posterOffset: false,
			time: 0,
			duration: 0,
			lastUpdate: 0,
			playerWidth: 0,
			timelineImageWidth: 0,
			progressPercent: 0,
			pixelsPerSecond: 0,
			spectWidth: 0,
            zoomLevel: 1,
			volumeSlider: 1,
			volumeToRestore: 1,
			showVolume: false,
			showLoading: true,
			stillProcessing: false,
			mediaMissing: false,
			specimenBaseUrl: 'https://macaulaylibrary.org/asset/',
			spects: [],
			xAxisCanv: null,
			yAxisCanv: null,
			trackCanv: null,
			timelineCanv: null,
			cursorCanv: null,
			yAxisWidth: this.minimal ? 0 : 24,
			width: 0,
			basePxPerSec: 250, // px/sec of the generated spectrograms
			locked: false,
			fillColor: '#6F6E67', // $brand-color-sand-11
			textColor: '#817F79', // $brand-color-sand-10
		};
	},
	props: {
		/**
		 * ID of the media provided by the database
		 */
		assetId: {}, // can be Number or String
		/**
		 * URL for the citation
		 */
		citationUrl: {
			default: false,
		},
		/**
		 * Name of the citation
		 */
		citationName: {
			default: null,
			type: String,
		},
		timeline: {
			default: false,
			type: Boolean,
		},
		zoom: {
			default: false,
			type: Boolean,
		},
		assetRestricted: {
			default: false,
			type: Boolean,
		},
		/**
		 * Name of the user
		 */
         user: {
			default: null,
			type: String,
		},
		/**
		 * Location of the user
		 */
		location: {
			default: null,
			type: String,
		},
		/**
		 * Date added or created
		 */
         date: {
			default: null,
			type: String,
		},
		/**
		 * Title of the component
		 */
		title: {
			default: null,
			type: String,
		},
		/**
		 * Indicates whether to play media automatically or not
		 */
		autoplay: {
			default: false,
			type: Boolean,
		},
		/**
		 * Size changes the height, can be 'sm' or 'xs'
		 */
		size: {
			default: '',
			type: String,
		},
		mediaDownloadUrl: {
			default: 'https://cdn.download.ams.birds.cornell.edu/api/v1/asset/',
			type: String,
		},
		/**
		 * Enables ability to view, draw, and edit boxes
		 */
		annotation: {
			type: Boolean,
			default: false,
		},
		/**
		 * Enables drawing and editing boxes
		 */
		enableAnnotating: {
			type: Boolean,
			default: false,
		},
		/**
		 * The initial set of annotations to render
		 */
		initialAnnotations: {
			type: Array,
			default() {
				return [];
			},
		},
		/**
		 * List of model predictions
		 */
		predictions: {
			type: Array,
			default() {
				return [];
			}
		},
		/**
		 * Annotation guides
		 */
		guides: {
			type: Array,
			default() {
				return [];
			}
		},
		/**
		 * Sets label, color, and any other data for the current annotation being drawn
		 */
		activeAnnotation: {
			type: Object,
			default() {
				return {};
			},
		},
		/**
		 * Annotations don't show white boxes for handles
		 */
		alternateHandles: {
			type: Boolean,
			default: false,
		},
		/**
		 * Allow user to drag edges
		 */
		 edgeHandles: {
			type: Boolean,
			default: false,
		},
		/**
		 * Skips to any time (in seconds)
		 */
		skipToTime: {
			type: Number,
			default: 0,
		},
		/**
		 * A way for the parent to skip back 2s
		 */
		skipBackIncrement: {
			type: Number,
			default: 0,
		},
		/**
		 * A way for the parent to skip ahead 2s
		 */
		skipAheadIncrement: {
			type: Number,
			default: 0,
		},
		/**
		 * A way for the parent to play and pause
		 */
		togglePlayIncrement: {
			type: Number,
			default: 0,
		},
		/**
		 * Which version of the spectrogram to load can be 1.0 for old or 2.0 for new
		 */
		version: {
			type: String,
			default: '1.0'
		},
		/**
		 * A way to start recording from any timestamp
		 */
		startAtTime: {
			type: Number,
			default: 0
		},
		/**
		 * A way to set a default pixel per second rate
		 */
		displayPxPerSec: {
			type: Number,
			default: 200,
		},
        /**
         * A way to set a max zoom in px/sec
         */
        maxPxPerSec: {
            type: Number,
            default: 400,
            validator: value => {
                // Only accepts values greater than px/sec of the generated spectrograms
                return (value > 250);
            },
        },
        /**
         * A way to set a min zoom in px/sec
         */
         minPxPerSec: {
            type: Number,
            default: 50,
            validator: value => {
                // Only accepts values less than px/sec of the generated spectrograms
                return (value < 250);
            },
        },
		/**
		 * Minimal design: Merlin player
		 */
		minimal: {
			type: Boolean,
			default: false,
		},
	},
	watch: {
		skipToTime(newTime) {
			this.time = newTime;
			this.$refs.audio.currentTime = newTime;
			this.setTranslation();
			this.redrawCanvas();
		},
		skipBackIncrement() {
			const currentTime = this.$refs.audio.currentTime;
			const newTime = Math.max(0, currentTime - 2);
			this.animateSkip(currentTime, newTime);
		},
		skipAheadIncrement() {
			const currentTime = this.$refs.audio.currentTime;
			const newTime = Math.min(currentTime + 2, this.duration);
			this.animateSkip(currentTime, newTime);
		},
		togglePlayIncrement() {
			this.togglePlay();
		},
		autoplay(autoplay) {
			this.needToAutoplay = autoplay
		}
	},
	computed: {
		poster() {
			if (this.version === '1.0') {
				return this.mediaDownloadUrl + this.assetId + '/poster';
			} else if (this.version === '2.0') {
				return `${this.mediaDownloadUrl}${this.assetId}/default/preview`;
			}
		},
		media() {
			if (this.version === '1.0') {
				return this.mediaDownloadUrl + this.assetId + '/audio';
			} else if (this.version === '2.0') {
				return this.mediaDownloadUrl + this.assetId + '/mp3';
			}
		},
		timelineImage() {
			if (this.version === '1.0') {
				return `url(${this.mediaDownloadUrl}${this.assetId}/spectrogram_small)`
			}
			return `url(${this.mediaDownloadUrl}${this.assetId}/default/timeline)`
		},
		specimenUrl() {
			// if citationUrl is false, the asset has no parent and specimenUrl is restricted
			// so link/branding should be hidden
			if (this.citationUrl || this.assetRestricted) {
				return false;
			} else {
				return this.specimenBaseUrl + this.assetId;
			}
		},
		frameHeight() {
			switch (this.size) {
				case 'compact':
				case 'sm':
					return 168;
					break;
				case 'xs':
					return 110;
					break;
				default:
					return 220;
			}
		},
		predictionsHeight() {
			const speciesPredictions = []
			this.predictions.forEach(prediction => {
				if (!speciesPredictions.includes(prediction.categoryId)) {
					speciesPredictions.push(prediction.categoryId)
				}
			})
			return speciesPredictions.length * 14
		},
		yAxisFont() {
			switch (this.size) {
				case 'xs':
					return '9px sans-serif';
					break;
				case 'sm':
					return '11px sans-serif';
					break;
				default:
					return '12px sans-serif';
			}
		},
		pxPerSec() {
			return this.zoom ? (this.basePxPerSec * this.zoomLevel) : this.displayPxPerSec;
		},
        baseZoomLevel() {
            // absolute max px/sec
			let max = Math.min(this.maxPxPerSec, 1.75 * this.basePxPerSec);
			// absolute min px/sec
			let min = Math.max(this.minPxPerSec, 0.15 * this.basePxPerSec);
			let pxPerPercent = (max - min) / 99;
            // intercept is at x = 1
			let intercept = min - pxPerPercent;
            return (this.displayPxPerSec - intercept) / pxPerPercent;
        },
		spectrogramWidthFactor() {
			return this.zoom ? this.zoomLevel : (this.displayPxPerSec / this.basePxPerSec);
		},
		maxFreq() {
			if (this.version === '1.0') {
				return 10;
			} else if (this.version === '2.0') {
				return 12;
			}
		},
		secondsPerSpect() {
			if (this.version === '1.0') {
				return 262;
			} else if (this.version === '2.0') {
				return 60;
			}
		},
		timelineHeight() {
			if (this.timeline) {
				if (this.version === '1.0') {
					return 40;
				} else if (this.version === '2.0') {
					return 40;
				}
			}
			else {
				return 0;
			}
		},
		xAxisHeight() {
			if (this.minimal) {
				return 0;
			}
			if (this.version === '1.0') {
				return 16;
			} else if (this.version === '2.0') {
				return 20;
			}
		},
		messages() {
			return {
				mute: 'mute',
				volume: 'volume',
				seek: 'seek',
				play: 'play',
				zoom: 'zoom',
				errorNoSpectrogram: 'Sorry, there was an error loading this file. Please check later.',
				errorNoAudio: 'We are still processing this file. Please check later.',
			};
		},
		classes() {
			const classes = [];
			const prefix = 'SpectrogramPlayer--';
			if (this.size.length) {
				classes.push(prefix + this.size);
			}
			if (this.annotation) {
				classes.push(prefix + 'annotation');
			}
			return classes.join(' ');
		},
		spectNeededToPlay() {
			return Math.floor(this.time / this.secondsPerSpect);
		}
	},
	beforeMount() {
		if (this.size === 'compact') {
			this.size == 'sm';
		}
        if (this.zoom && this.displayPxPerSec !== this.basePxPerSec) {
            this.zoomLevel = this.zoomValToZoomLevel(this.baseZoomLevel);
        }
		this.needToAutoplay = !!this.autoplay
	},
	mounted() {
		this.dpr = window.devicePixelRatio
		// listeners for resize, togglePlay, pause, destroy
		window.addEventListener('resize', this.handleResize);
		document.addEventListener('lichen:spect-togglePlay', this.handleTogglePlayEvent);
		document.addEventListener('lichen:spect-pause', this.pause);
		document.addEventListener('lichen:spect-destroy', this.destroyPlayer);
	},
	beforeDestroy() {
		this.destroyPlayer();
	},
	methods: {
		removeListeners() {
			window.removeEventListener('resize', this.handleResize);
			document.removeEventListener('lichen:spect-togglePlay', this.handleTogglePlayEvent);
			document.removeEventListener('lichen:spect-pause', this.pause);
			document.removeEventListener('lichen:spect-destroy', this.destroyPlayer);
		},
		destroyPlayer(e) {
			if (e?.detail.assetId === parseInt(this.assetId)) {
				if (this.playing) {
					this.pause();
				}
			}
			this.removeListeners();
		},
		handleTogglePlayEvent(e) {
			if (e.detail.assetId === parseInt(this.assetId)) {
				this.togglePlay();
			}
		},
		setVolume(level) {
			this.$refs.audio.volume = level;
			this.volumeToRestore = level;
		},
		togglePlay() {
			if (!this.showSpectrogramPlayer) {
				this.showSpectrogramPlayer = true;
				this.drawYAxis();
			}
			if (this.$refs.audio) {
				if (this.playing) {
					this.$refs.audio.pause();
					this.$emit('playing', false);
				} else {
					this.$refs.audio.currentTime = this.time;
					this.$refs.audio.play();
					this.$emit('playing', true);
				}
			}
		},
		isPlaying() {
			this.playing = true;
			this.animateCanvas();
			this.$emit('playing', true);
		},
		isPaused() {
			this.playing = false;
			this.$emit('playing', false);
		},
		pause() {
			if (this.$refs.audio) {
				this.$refs.audio.pause();
			}
			this.playing = false;
		},
		animateCanvas() {
			const loop = () => {
				// measure how long it's been since the last loop
				const timeSinceLastUpdate = performance.now() - this.lastUpdate;
				this.lastUpdate = performance.now();
				this.time += timeSinceLastUpdate / 1000;
				this.setTranslation();
				this.redrawCanvas();
				if (this.playing) {
					// recursively call recursive function
					requestAnimationFrame(loop);
				}
			};
			this.lastUpdate = performance.now();
			requestAnimationFrame(loop);
		},
		// clicking on the seek slider, or adjusting the slider (end of drag)
		setTime(value) {
			this.time = parseFloat(value);
			this.$refs.audio.currentTime = parseFloat(value);
			this.setTranslation();
			this.redrawCanvas();
		},
		// adjusting the seek slider (during drag)
		setPosition(value) {
			this.time = parseFloat(value);
			this.setTranslation();
			this.redrawCanvas();
		},
		setTranslation() {
			this.position = (this.width / 2) + (this.time * this.pxPerSec);
		},
		setZoom(x) {
			// x is a value between 1 and 100
			// 50 is zoom of 1
			let newZoom = 1;
			// absolute max px/sec
			let max = Math.min(this.maxPxPerSec, 1.75 * this.basePxPerSec);
			// absolute min px/sec
			let min = Math.max(this.minPxPerSec, 0.15 * this.basePxPerSec);
			let pxPerPercent = (max - min) / 99;
            // intercept is at x = 1
			let intercept = min - pxPerPercent;
			newZoom = ((pxPerPercent * x) + intercept) / this.basePxPerSec;
			this.zoomLevel = newZoom;
            this.setTranslation();
			this.redrawCanvas();
		},
        zoomValToZoomLevel(x) {
			// x is a value between 1 and 100
			// 50 is zoom of 1
			let newZoom = 1;
			// absolute max px/sec
			let max = Math.min(this.maxPxPerSec, 1.75 * this.basePxPerSec);
			// absolute min px/sec
			let min = Math.max(this.minPxPerSec, 0.15 * this.basePxPerSec);
			let pxPerPercent = (max - min) / 99;
            // intercept is at x = 1
			let intercept = min - pxPerPercent;
			newZoom = ((pxPerPercent * x) + intercept) / this.basePxPerSec;
			return newZoom;
		},
		redrawCanvas() {
			this.progressPercent = 100 * this.time / this.duration;
			this.drawXAxis();
			this.drawTimeline();
			this.drawTrack();
			this.drawCursor();
		},
		// time values should be formatted as 0:00 or 0:00:00
		formatTime(time) {
			if (time < 3600) {
				return Math.floor(time / 60) + ':' + ('0' + Math.floor(time % 60)).slice(-2);
			}
			return (
				Math.floor(time / 3600) +
				':' +
				('0' + Math.floor((time % 3600) / 60)).slice(-2) +
				':' +
				('0' + Math.floor(time % 60)).slice(-2)
			);
		},
		// the audio player sends an event with currentTime every 1/4 second or so, and we resync
		timeUpdated(e) {
			//console.log('pixPerSecond', this.pixelsPerSecond);
			if (this.$refs.audio) {
				this.time = this.$refs.audio.currentTime;
				this.$emit('time-updated', this.time);
			}
		},
		prepareSpects() {
			if (this.version === '1.0') {
				this.spects.push({
					url: this.mediaDownloadUrl + this.assetId + '/spectrogram_small',
					image: null,
					timer: null
				});
			} else if (this.version === '2.0') {
				const numSpects = Math.ceil(this.duration / this.secondsPerSpect);
				for (let i = 0; i < numSpects; i++) {
					let index = String(i + 1).padStart(4, '0');
					this.spects.push({
						url: `${this.mediaDownloadUrl}${this.assetId}/default/partial/${index}`,
						image: null,
						timer: null
					});
				}
			}
		},
		loadSpect(index) {
			const start = Math.max(0, Math.floor((this.position - this.width) / (this.pxPerSec * this.secondsPerSpect)));
			const end = Math.ceil((this.position + this.width / 2) / (this.pxPerSec * this.secondsPerSpect));
			if (index < start || index > end) {
				return;
			}
			this.spects[index].image = 'loading';
			const image = new Image();
			image.spectIndex = index;
			image.addEventListener('load', this.drawSpect);
			image.addEventListener('error', this.spectError);
			image.src = this.spects[index].url;
		},
		drawSpect(e) {
			const image = e.target;
			this.spects[image.spectIndex].image = image;
			const offset = (this.time < (this.width / ( 2 * this.pxPerSec)) && !this.annotation) ? 
				(image.spectIndex * this.pxPerSec * this.secondsPerSpect) : 
				(image.spectIndex * this.pxPerSec * this.secondsPerSpect + this.width - this.position);
			this.trackCanv.drawImage(image, offset, 0, this.spects[image.spectIndex].image.width * this.spectrogramWidthFactor, this.frameHeight);
			if (this.spectNeededToPlay === image.spectIndex) {
				//console.log('ready to play spect', image.spectIndex);
				this.showLoading = false;
				// needToAutoplay becomes false after first time playing, so that loading other spects doesn't cause playing
				this.readyToPlaySpect = true;
				if (this.needToAutoplay) {
					this.startPlayIfReady();
				}
			}
		},
		spectError(e) {
			console.error('Error loading spectrogram image', e.target.spectIndex);
		},
		drawXAxis() {
			this.xAxisCanv.setTransform(1, 0, 0, 1, 0, 0)
			this.xAxisCanv.scale(this.dpr, this.dpr)
			this.xAxisCanv.clearRect(0, 0, this.width, this.xAxisHeight);
			let start = Math.floor((this.position - this.width) / this.pxPerSec);
			if (start < 0) {
				start = 0;
			}
			let end = Math.ceil((this.position + this.width) / this.pxPerSec);
			if (end > this.duration) {
				end = this.duration;
			}
			// only draw ticks for times that are visible
			for (let time = start; time <= end; time += 0.25) {
				const x = (this.time < (this.width / (2 * this.pxPerSec)) && !this.annotation) ? 
					(time * this.pxPerSec) : 
					(time * this.pxPerSec + this.width - this.position);
				this.xAxisCanv.beginPath();
				this.xAxisCanv.moveTo(x, 0);
				if (time % 5 === 0) {
					this.xAxisCanv.lineTo(x, 16);
				} else if (time % 1 === 0) {
					if (this.zoomLevel > 0.2) {
						this.xAxisCanv.lineTo(x, 16);
					} else {
						this.xAxisCanv.lineTo(x, 4);
					}
				} else if (time % 1 === 0.5 && this.zoomLevel > 0.5) {
					this.xAxisCanv.lineTo(x, 8);
				} else if (time % 0.25 === 0 && this.zoomLevel > 0.75) {
					this.xAxisCanv.lineTo(x, 4);
				}
				this.xAxisCanv.stroke();
				this.xAxisCanv.closePath();
				if (time % 1 === 0 && this.zoomLevel > 0.2) {
					this.xAxisCanv.fillText(this.formatTime(time), x + 4, 14);
				} else if (time % 5 === 0) {
					this.xAxisCanv.fillText(this.formatTime(time), x + 4, 14);
				}
			}
		},
		drawYAxis() {
			this.yAxisCanv.setTransform(1, 0, 0, 1, 0, 0)
			this.yAxisCanv.scale(this.dpr, this.dpr)
			this.yAxisCanv.clearRect(0, 0, this.yAxisWidth, this.frameHeight + this.xAxisHeight);
			this.yAxisCanv.font = this.yAxisFont;
			this.yAxisCanv.fillStyle = this.fillColor;
			this.yAxisCanv.lineWidth = 1;
			this.yAxisCanv.strokeStyle = this.textColor;
			const yAxisHeight = this.frameHeight;
			let freq = this.maxFreq;
			while (freq >= 0) {
				const y = this.xAxisHeight + yAxisHeight - (freq / this.maxFreq) * yAxisHeight;
				this.yAxisCanv.beginPath();
				this.yAxisCanv.moveTo(0, y);
				this.yAxisCanv.lineTo(this.yAxisWidth / 2, y);
				this.yAxisCanv.stroke();
				this.yAxisCanv.closePath();
				if (freq === 0) {
					this.yAxisCanv.fillText('kHz', 2, y - 4 + (this.size == 'xs'));
				} else {
					this.yAxisCanv.fillText(freq, 2, y + 14 - 3 * (this.size == 'xs'));
				}
				freq -= 2;
			}
		},
		drawTimeline() {
			this.timelineCanv.setTransform(1, 0, 0, 1, 0, 0)
			this.timelineCanv.scale(this.dpr, this.dpr)
			this.timelineCanv.clearRect(0, 0, this.width + this.yAxisWidth, this.timelineHeight);
			const x = (this.time / this.duration) * (this.width + this.yAxisWidth);
			this.timelineCanv.beginPath();
			this.timelineCanv.moveTo(x, 0);
			this.timelineCanv.lineTo(x, this.timelineHeight);
			this.timelineCanv.stroke();
			this.timelineCanv.closePath();
			let timelinePxInView = ((this.width / (this.pxPerSec * this.duration)) * (this.width + this.yAxisWidth));
			this.timelineCanv.fillStyle = "rgba(0, 0, 0, 0.1)";
			this.timelineCanv.fillRect(0, 0, x - timelinePxInView / 2, this.timelineHeight);
			this.timelineCanv.fillRect(x + timelinePxInView / 2, 0, (this.width + this.yAxisWidth) - (x + timelinePxInView / 2), this.timelineHeight);
		},
		drawTrack() {
			this.trackCanv.setTransform(1, 0, 0, 1, 0, 0)
			this.trackCanv.scale(this.dpr, this.dpr)
			this.trackCanv.clearRect(0, 0, this.width, this.frameHeight);
			// calculate which spectrograms are in view
			const start = Math.max(0, Math.floor((this.position - this.width) / (this.pxPerSec * this.secondsPerSpect)));
			const end = Math.ceil((this.position + this.width / 2) / (this.pxPerSec * this.secondsPerSpect));
			for (let i = start; i < end; i++) {
				if (!this.spects[i]) {
					return
				}
				if (this.spects[i].image === null) {
					// delay loading by 50ms, loadSpect() checks if still in view before loading
					this.spects[i].timer = setTimeout(() => {
						this.loadSpect(i)
					}, 50)
				}
				if (this.spects[i].image !== 'loading' && this.spects[i].image !== null) {			
					const offset = (this.time < (this.width / ( 2 * this.pxPerSec)) && !this.annotation) ? 
						(i * this.pxPerSec * this.secondsPerSpect) : 
						(i * this.pxPerSec * this.secondsPerSpect + this.width - this.position);
					this.trackCanv.drawImage(this.spects[i].image, offset, 0, this.spects[i].image.width * this.spectrogramWidthFactor, this.frameHeight);
				}
			}
		},
		drawCursor() {
			this.cursorCanv.setTransform(1, 0, 0, 1, 0, 0)
			this.cursorCanv.scale(this.dpr, this.dpr)
			this.cursorCanv.clearRect(0, 0, this.width, this.frameHeight + this.xAxisHeight);
			this.cursorCanv.lineWidth = 1;
			const offset = (this.time < (this.width / (2 * this.pxPerSec)) && !this.annotation) ? (this.time * this.pxPerSec) : (this.width / 2);
			this.cursorCanv.beginPath();
			this.cursorCanv.moveTo(offset, 0);
			this.cursorCanv.lineTo(offset, this.frameHeight + this.xAxisHeight);
			this.cursorCanv.stroke();
			this.cursorCanv.closePath();
		},
		drawCanvas() {
			const rect = this.$el.getBoundingClientRect();
			this.width = rect.width - this.yAxisWidth - 2; // -2 for border
			this.$nextTick(() => {
				this.xAxisCanv = this.$refs.xAxisCanv.getContext('2d');
				this.trackCanv = this.$refs.trackCanv.getContext('2d');
				this.timelineCanv = this.$refs.timelineCanv.getContext('2d');
				this.cursorCanv = this.$refs.cursorCanv.getContext('2d');
				this.yAxisCanv = this.$refs.yAxisCanv.getContext('2d');
				this.trackCanv.imageSmoothingEnabled = false;
				this.position = this.width / 2 + this.time * this.pxPerSec;
				this.xAxisCanv.font = '12px sans-serif';
				this.xAxisCanv.fillStyle = this.fillColor;
				this.xAxisCanv.lineWidth = 1;
				this.xAxisCanv.strokeStyle = this.textColor;
				this.drawTrack();
				this.drawXAxis();
				this.drawYAxis();
				this.drawTimeline();
				this.drawCursor();
			});
		},
		// <audio preload="metadata"/> means we get an event when the player gets the file's metadata
		// we only need duration, and use this value to set other values, and sometimes trigger autoplay
		loadedMetadata(e) {
			const duration = e.target.duration;
			this.duration = duration;
			this.time = this.startAtTime;
			this.$refs.audio.currentTime = this.startAtTime;
			this.$emit('spectrogram-duration', duration);
			if (this.annotation) {
				this.showSpectrogramPlayer = true;
			}
			this.prepareSpects();
			this.drawCanvas();
			this.updateTimelineWidth();
			this.mediaMissing = false;
			this.stillProcessing = false;
			if (!this.autoplay) {
				this.showLoading = false;
			}
		},
		startPlayIfReady() {
			// needed for autoplay
			//console.log('start play if ready', 'spect?', this.readyToPlaySpect, 'audio?', this.readyToPlayAudio)
			if (this.readyToPlaySpect && this.readyToPlayAudio && this.$refs.audio) {
				let promise = this.$refs.audio.play();
				if (promise !== undefined) {
					promise
						.catch((error) => {
							// no play
						})
						.then(() => {
							// yes play
							this.needToAutoplay = false;
							this.$emit('playing', true);
						});
				}
				this.showLoading = false;
			}
		},
		canPlayThrough() {
			//console.log('can play through');
			this.readyToPlayAudio = true;
			//console.log('ready to play audio')
			if (this.autoplay) {
				this.startPlayIfReady();
			}
		},
		handleResize() {
			this.dpr = window.devicePixelRatio
			const rect = this.$el.getBoundingClientRect();
			this.width = rect.width - this.yAxisWidth - 2; // -2 for border
			this.updateTimelineWidth();
			if (!this.xAxisCanv) {
				return
			}
			this.$nextTick(() => {
				this.setTranslation();
				this.redrawCanvas();
			});
		},
		updateTimelineWidth() {
			this.timelineImageWidth = this.width + this.yAxisWidth;
			if (this.version === '1.0') {
				const maxDuration = 65500.0 / this.basePxPerSec;
				if (this.duration > maxDuration) {
					this.timelineImageWidth = (maxDuration / this.duration) * (this.width + this.yAxisWidth);
				}
			}
		},
		toggleVolume() {
			if (this.volumeSlider == 0) {
				if (this.volumeToRestore == 0) {
					this.volumeSlider = 1;
				} else {
					this.volumeSlider = this.volumeToRestore;
				}
			} else {
				this.volumeSlider = 0;
			}
			this.setVolume();
		},
		clickOnSpectrogramPlayer(e) {
			if (!this.annotation) {
				this.togglePlay();
			}
		},
		saveAnnotation(payload) {
			this.$emit('annotation-changed', payload);
		},
		annotationSelected(index, e) {
			this.$emit('annotation-selected', index, e);
		},
		animateSkip(oldValue, newValue) {
			if (!this.locked) {
				this.locked = true;
				const wasPlaying = this.playing;
					if (wasPlaying) {
						this.pause();
					}
					const time = 0.2; // seconds
					let currentTime = 0;
					const tick = () => {
						currentTime += 1 / 60;
						const p = currentTime / time;
						// ease in and out
						const t = -0.5 * (Math.cos(Math.PI * p) - 1);
						if (p < 1) {
							requestAnimationFrame(tick);
							this.$refs.audio.currentTime = oldValue + (newValue - oldValue) * t;
						} else {
							// scroll is done
							this.$refs.audio.currentTime = newValue;
							this.time = newValue;
						}
						this.setTranslation();
						this.redrawCanvas();
					};
					tick();
					if (wasPlaying) {
						this.togglePlay();
					}
				this.locked = false;
			}
		},
	},
};
</script>

<style lang="scss">
.SpectrogramPlayer {
	text-align: left;
	overflow: hidden;
	background-color: $color-white;
	user-select: none;
	canvas {
		image-rendering: pixelated;
	}

	.SpectrogramPlayer-frame {
		position: relative;
		border: 1px solid $color-neutral-2;
		border-bottom: 0;
		display: grid;
		grid-template-areas:
		'yAxis xAxis'
		'yAxis track'
		'yAxis predictions'
		'timeline timeline';
	}

	// canvases
	.yAxisCanv {
		display: block;
		grid-area: yAxis;
		background-color: $color-white;
		z-index: 1;
	}
	.xAxisCanv {
		display: block;
		grid-area: xAxis;
	}
	.trackCanv {
		display: block;
		grid-area: track;
	}
	.cursorCanv {
		display: block;
		grid-area: xAxis / predictions;
		pointer-events: none;
	}
	.timelineCanv {
		display: block;
		grid-area: timeline / timeline;
		background-repeat: no-repeat;
	}
	.SpectrogramPlayer-predictions {
		display: block;
		grid-area: predictions;
	}

	// image shown before playing
	.SpectrogramPlayer-poster {
		position: absolute;
		cursor: pointer;
		object-fit: fill;
		object-position: left;
	}

	// error message
	.SpectrogramPlayer-errorMessage {
		@include centerCenter;
		@include inset-md;
		color: $color-neutral-5;
		background-color: $color-overlay-white;
	}

	// spinner
	.LoadingAnimation {
		@include centerCenter;
		background-color: transparent;
		pointer-events: none;
	}
}

// color variants
.SpectrogramPlayer--primary {
	$spect-primary-dark: darken($color-primary-5, 5%);
	.MediaControls-controls {
		background-color: $spect-primary-dark;
	}
	.MediaControls-branding {
		display: none;
	}
	.MediaControls-volume .MediaControls-volumeSliderOverlay {
		background-color: $spect-primary-dark;
	}
	.MediaControls-meta {
		a {
			color: $color-link-reverse;
		}
	}
}

// add border under x axis
.SpectrogramPlayer--annotation {
	// the x axis
	.SpectrogramPlayer-xAxis {
		border-bottom: 1px solid $color-neutral-1;
		.SpectrogramPlayer-tick {
			&:nth-child(4n + 1) {
				background-color: transparent;
			}
		}
	}
}

// Playlist component uses this variant to show asset metadata in the control bar
.SpectrogramPlayer--withMeta {
	.MediaControls-meta {
		.MediaControls-title {
			//@include h5;
			@include stack-xs;
			&:empty {
				display: none;
			}
		}
	}
	@supports (grid-area: x) {
		.MediaControls-controls {
			@include inset-squish-sm;
			display: grid;
			grid-template-areas:
				'play volume meta'
				'time blank meta';
			grid-gap: $space-sm;
			grid-template-columns: auto auto 1fr;
			grid-template-rows: auto 1fr;
		}
		.MediaControls-playButton {
			grid-area: play;
		}
		.MediaControls-time {
			grid-area: time;
			align-self: start;
			@include inset-squish-sm;
		}
		.MediaControls-volume {
			grid-area: volume;
		}
		.MediaControls-meta {
			grid-area: meta;
			@include inset-sm;
		}
	}
}
</style>
